/**
 * Who runs a Standard job, and why a run stopped (DESIGN UD-38). A Standard job runs in the page, so two things are tracked
 * outside the job record:
 *
 * - **A run lock** (Web Locks API, one lock per job id, held for the life of the run). A second tab that opens the app finds
 *   the job's lock held and shows "running in another tab" instead of offering a Resume that would start a second run of the
 *   same job and pay twice. The browser frees the lock when the page goes away, so a job with no holder is one whose runner
 *   is gone. Where `navigator.locks` is missing nothing is held and nothing can be read: the caller falls back to the
 *   earlier behaviour (every other `running` Standard job reads as paused).
 * - **A seen record** in `localStorage` (synchronous, so it survives `pagehide`): the page's state (visible, hidden, being
 *   unloaded, offline), when it last changed and when the page was last alive. After a run is lost the record says how:
 *   closed or reloaded, in the background, offline, or nothing known (the computer slept).
 *
 * Both take their environment as arguments, so Node tests can drive them.
 */
import type { Seen, SeenState } from './job.ts';

const LOCK_PREFIX = 'provenance-bench-job:';
const SEEN_PREFIX = 'provenance-bench-seen:';

export const lockName = (jobId: string): string => `${LOCK_PREFIX}${jobId}`;
export const seenKey = (jobId: string): string => `${SEEN_PREFIX}${jobId}`;

/** The part of `navigator.locks` used here. */
export interface LockApi {
	request(
		name: string,
		options: { ifAvailable: true },
		callback: (lock: unknown) => Promise<void> | undefined
	): Promise<unknown>;
	query(): Promise<{ held?: ReadonlyArray<{ name?: string | null }> }>;
}

export interface RunLock {
	/** Frees the lock (harmless to call twice). The browser frees it anyway when the page goes away. */
	release(): void;
}

const systemLocks = (): LockApi | null => {
	const l = typeof navigator === 'undefined' ? undefined : (navigator as { locks?: unknown }).locks;
	return l ? (l as LockApi) : null;
};

/**
 * Takes the job's run lock: the lock, or null when another page holds it. Where the Web Locks API is missing (or fails to
 * answer) the run goes ahead unprotected, as before.
 */
export function acquireRunLock(
	jobId: string,
	locks: LockApi | null = systemLocks()
): Promise<RunLock | null> {
	const unprotected: RunLock = { release() {} };
	if (!locks) return Promise.resolve(unprotected);
	return new Promise((resolve) => {
		let release: () => void = () => {};
		const held = new Promise<void>((r) => {
			release = r;
		});
		locks
			.request(lockName(jobId), { ifAvailable: true }, (lock) => {
				if (!lock) {
					resolve(null);
					return undefined;
				}
				resolve({ release });
				return held;
			})
			.catch(() => resolve(unprotected));
	});
}

/** The ids of the jobs whose run lock is held by some page, this one included; null when the lock state cannot be read. */
export async function heldJobIds(
	locks: LockApi | null = systemLocks()
): Promise<Set<string> | null> {
	if (!locks) return null;
	try {
		const state = await locks.query();
		const ids = new Set<string>();
		for (const h of state.held ?? [])
			if (h.name?.startsWith(LOCK_PREFIX)) ids.add(h.name.slice(LOCK_PREFIX.length));
		return ids;
	} catch {
		return null;
	}
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const systemStore = (): Store | null => {
	try {
		return typeof localStorage === 'undefined' ? null : localStorage;
	} catch {
		return null;
	}
};

const STATES: readonly SeenState[] = ['visible', 'hidden', 'pagehide', 'offline'];

/** The seen record of a job, or null: none stored, unreadable, or not in the shape written here. */
export function readSeen(jobId: string, store: Store | null = systemStore()): Seen | null {
	try {
		const raw = store?.getItem(seenKey(jobId));
		if (!raw) return null;
		const v = JSON.parse(raw) as Partial<Seen>;
		const ok =
			typeof v.since === 'string' &&
			typeof v.at === 'string' &&
			STATES.includes(v.state as SeenState);
		return ok ? (v as Seen) : null;
	} catch {
		return null;
	}
}

/**
 * The page's state from what it knows: being unloaded beats offline, offline beats hidden. (A page that went offline and was
 * then hidden is reported as offline: that is the more useful reason for a run that stopped.)
 */
export const seenState = (f: {
	unloading: boolean;
	online: boolean;
	hidden: boolean;
}): SeenState =>
	f.unloading ? 'pagehide' : !f.online ? 'offline' : f.hidden ? 'hidden' : 'visible';

/** The next record: `since` moves only when the state changes; `at` is always now. */
export function nextSeen(prev: Seen | null, state: SeenState, nowIso: string): Seen {
	return { state, since: prev && prev.state === state ? prev.since : nowIso, at: nowIso };
}

export interface WatchEnv {
	win: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
	doc: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> & { visibilityState: string };
	online: () => boolean;
	store: Store | null;
	now: () => number;
	every: (fn: () => void, ms: number) => () => void;
}

const systemEnv = (): WatchEnv => ({
	win: window,
	doc: document,
	online: () => navigator.onLine,
	store: systemStore(),
	now: () => Date.now(),
	every: (fn, ms) => {
		const t = setInterval(fn, ms);
		return () => clearInterval(t);
	},
});

/** How often a running page says it is alive. A sleeping computer stops it; the last beat dates the stop. */
export const HEARTBEAT_MS = 15_000;

export interface Watch {
	/** The page's state as last recorded. */
	state(): Seen;
	/** Stops watching and forgets the record: the run ended in a way the job itself records. */
	stop(): void;
}

/**
 * Records the page's state while a Standard job runs: on every `visibilitychange`, `pagehide`, `pageshow`, `online` and
 * `offline`, and every `HEARTBEAT_MS`. Writes are synchronous so the last one survives an unload. Without storage nothing is
 * recorded and a lost run reads as "stopped, cause unknown".
 */
export function watchRun(jobId: string, env: WatchEnv = systemEnv()): Watch {
	let unloading = false;
	let seen = nextSeen(
		null,
		seenState({ unloading, online: env.online(), hidden: env.doc.visibilityState === 'hidden' }),
		new Date(env.now()).toISOString()
	);
	const write = (): void => {
		seen = nextSeen(
			seen,
			seenState({ unloading, online: env.online(), hidden: env.doc.visibilityState === 'hidden' }),
			new Date(env.now()).toISOString()
		);
		try {
			env.store?.setItem(seenKey(jobId), JSON.stringify(seen));
		} catch {
			/* storage full or blocked: the reason stays unknown */
		}
	};
	const onHide = (): void => {
		unloading = true;
		write();
	};
	const onShow = (): void => {
		unloading = false;
		write();
	};
	write();
	env.doc.addEventListener('visibilitychange', write);
	env.win.addEventListener('pagehide', onHide);
	env.win.addEventListener('pageshow', onShow);
	env.win.addEventListener('online', write);
	env.win.addEventListener('offline', write);
	const stopBeat = env.every(write, HEARTBEAT_MS);
	return {
		state: () => seen,
		stop() {
			stopBeat();
			env.doc.removeEventListener('visibilitychange', write);
			env.win.removeEventListener('pagehide', onHide);
			env.win.removeEventListener('pageshow', onShow);
			env.win.removeEventListener('online', write);
			env.win.removeEventListener('offline', write);
			try {
				env.store?.removeItem(seenKey(jobId));
			} catch {
				/* nothing to remove */
			}
		},
	};
}
